import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Card, CardContent, Link, Typography } from '@mui/material';

import type { SyntaxHighlighterProps } from 'react-syntax-highlighter';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

import ErrorChat, { ErrorNode } from 'pages/Chat/ErrorsChat';
import { AnalysisView } from 'pages/Chat/AnalysisView';
import { ActionsView } from 'pages/Chat/ActionsView';
import { TradeSetup } from 'pages/Chat/TradeSetup';
import { ExecutionChat } from 'pages/Chat/ExecutionChat';
import { StatusCheck } from 'pages/Chat/StatusCheck';

export type TradingAction = {
	id?: string;
	data?: string;
	label?: string;
};

export type XMLProp = {
	name?: string;
	unit?: string;
	format?: string;
	value?: string;
};

export type XMLSection = {
	title?: string;
	props: XMLProp[];
	notes: string[];
};

export type XMLCard = {
	title?: string;
	props: XMLProp[];
	sections: XMLSection[];
	notes: string[];
};

export type AnalysisNode = {
	kind: 'analysis';
	cards: XMLCard[];
};

export type TradeSetupNode = {
	kind: 'trade_setup';
	cards: XMLCard[];
};

export type ExecutionNode = {
	kind: 'execution';
	cards: XMLCard[];
};

export type StatusCheckNode = {
	kind: 'status_check';
	cards: XMLCard[];
};

export type ActionsNode = {
	kind: 'actions';
	actions: TradingAction[];
};

export type TradingSequenceNode =
	| AnalysisNode
	| TradeSetupNode
	| ExecutionNode
	| StatusCheckNode
	| ErrorNode
	| ActionsNode;

export type OnActionType = (
	actionId: string,
	data?: string,
	label?: string,
	raw?: string,
) => void;

export interface ResponseRendererProps {
	text: string; // dynamic server response (XML/JSON/Markdown)
	onAction?: OnActionType;
	isCurrent?: boolean;
}

const PrismHighlighter =
	SyntaxHighlighter as unknown as React.ComponentType<SyntaxHighlighterProps>;

const isLikelyXML = (s: string) => {
	const t = (s || '').trim();
	return t.startsWith('<') && /<\s*trading_response[\s>]/i.test(t);
};

const isLikelyJSON = (s: string) => {
	const t = (s || '').trim();
	if (!t) return false;
	if (!(t.startsWith('{') || t.startsWith('['))) return false;
	try {
		JSON.parse(t);
		return true;
	} catch {
		return false;
	}
};

function getAttr(tag: string, attr: string): string | undefined {
	const m = new RegExp(`${attr}="([^"]*)"`).exec(tag);
	return m?.[1];
}

function getFirstTagBlock(xml: string, tag: string): string | undefined {
	const re = new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'i');
	const m = re.exec(xml);
	return m?.[0];
}

function getAllTagBlocks(xml: string, tag: string): string[] {
	const re = new RegExp(`<${tag}[^>]*>[\\s\\S]*?<\\/${tag}>`, 'gi');
	const out: string[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(xml))) out.push(m[0]);
	return out;
}

function innerText(block?: string): string | undefined {
	if (!block) return undefined;
	const m = /<[^>]+>([\s\S]*?)<\/[^>]+>$/.exec(block);
	return m?.[1]?.trim();
}

function stripOuter(block?: string): string | undefined {
	return innerText(block);
}

function parsePropBlocks(xml: string): XMLProp[] {
	const re = /<(prop)([^>]*)>([\s\S]*?)<\/prop>/gi;
	const props: XMLProp[] = [];
	let m: RegExpExecArray | null;
	while ((m = re.exec(xml))) {
		const open = m[0].slice(0, m[0].indexOf('>') + 1);
		props.push({
			name: getAttr(open, 'name'),
			unit: getAttr(open, 'unit'),
			format: getAttr(open, 'format'),
			value: (m[3] || '').trim(),
		});
	}
	return props;
}

function parseSectionBlock(block: string): XMLSection {
	const open = block.slice(0, block.indexOf('>') + 1);
	const title = getAttr(open, 'title') || undefined;
	const props = parsePropBlocks(block);
	const notes =
		(getAllTagBlocks(block, 'note')
			.map(stripOuter)
			.filter(Boolean) as string[]) || [];
	return { title, props, notes };
}

function parseCardBlock(block: string): XMLCard {
	const title = stripOuter(getFirstTagBlock(block, 'title'));
	const directProps = parsePropBlocks(block);
	const sectionBlocks = getAllTagBlocks(block, 'section');
	const sections = sectionBlocks.map(parseSectionBlock);
	const notes =
		(getAllTagBlocks(block, 'note')
			.map(stripOuter)
			.filter(Boolean) as string[]) || [];
	return { title, props: directProps, sections, notes };
}

function parseActionsBlock(block: string): ActionsNode {
	const actionRe = /<(action)([^>]*)>([^<]*)<\/action>/gi;
	const actions: TradingAction[] = [];
	let m: RegExpExecArray | null;

	while ((m = actionRe.exec(block))) {
		const openTag = m[0].slice(0, m[0].indexOf('>') + 1);
		actions.push({
			id: getAttr(openTag, 'id'),
			data: getAttr(openTag, 'data'),
			label: (m[3] || '').trim(),
		});
	}
	return { kind: 'actions', actions };
}

function parseCardsUnder(block: string): XMLCard[] {
	const cards = getAllTagBlocks(block, 'card').map(parseCardBlock);

	// Fallback: if no <card> but there are props/notes directly, wrap into one card
	if (!cards.length) {
		const props = parsePropBlocks(block);
		const notes =
			(getAllTagBlocks(block, 'note')
				.map(stripOuter)
				.filter(Boolean) as string[]) || [];
		if (props.length || notes.length)
			return [{ title: undefined, props, sections: [], notes }];
	}

	return cards;
}

function getTagBlock(block: string, tagName: string): string | null {
	const regex = new RegExp(`<${tagName}>(.*?)</${tagName}>`, 's');
	const match = block.match(regex);
	return match ? match[1] : null;
}

function getTagContent(block: string, tagName: string): string | null {
	const regex = new RegExp(`<${tagName}>(.*?)</${tagName}>`, 's');
	const match = block.match(regex);
	return match ? match[1].trim() : null;
}

function parseErrorBlock(block: string): { title: string; message: string } {
	const errorBlock = getTagBlock(block, 'error');
	if (!errorBlock) return { title: '', message: '' };

	const title = getTagContent(errorBlock, 'title') || 'Error';
	const details = getTagContent(errorBlock, 'details') || '';

	return { title, message: details };
}

function parseTradingXML(
	xmlStr: string,
): { sequence: TradingSequenceNode[]; raw: string } | null {
	try {
		if (!xmlStr || !isLikelyXML(xmlStr)) return null;
		const rootBlock = getFirstTagBlock(xmlStr, 'trading_response');
		if (!rootBlock) return null;

		const childRe =
			/<(analysis|trade_setup|execution|status_check|error|actions)\b[^>]*>[\s\S]*?<\/\1>/gi;

		const sequence: TradingSequenceNode[] = [];
		let m: RegExpExecArray | null;

		while ((m = childRe.exec(rootBlock))) {
			const tag = m[1] as TradingSequenceNode['kind'];
			const block = m[0];

			if (tag === 'actions') {
				sequence.push(parseActionsBlock(block));
				continue;
			}

			if (tag === 'error') {
				const errorInfo = parseErrorBlock(block);
				sequence.push({
					kind: 'error',
					message: errorInfo.message || 'Unknown error occurred',
					title: errorInfo.title || 'Error',
				});
				continue;
			}

			const cards = parseCardsUnder(block);

			if (tag === 'analysis') sequence.push({ kind: 'analysis', cards });
			else if (tag === 'trade_setup')
				sequence.push({ kind: 'trade_setup', cards });
			else if (tag === 'execution')
				sequence.push({ kind: 'execution', cards });
			else if (tag === 'status_check')
				sequence.push({ kind: 'status_check', cards });
		}

		return { sequence, raw: rootBlock };
	} catch {
		return null;
	}
}

/**
 * react-markdown can pass legacy `ref` (string possible).
 * MUI components don't accept string refs. Strip `node` and `ref`.
 */
function stripMarkdownProps<P extends { node?: unknown; ref?: unknown }>(
	props: P,
) {
	const { node, ref, ...rest } = props as any;
	void node;
	void ref;
	return rest as Omit<P, 'node' | 'ref'>;
}

const MarkdownBlock: React.FC<{ text: string }> = ({ text }) => (
	<Box
		sx={{
			'& h1': { fontSize: '1.5rem', mt: 0.5, mb: 1, fontWeight: 700 },
			'& h2': { fontSize: '1.25rem', mt: 1, mb: 0.5, fontWeight: 600 },
			'& h3': { fontSize: '1.1rem', mt: 1, mb: 1, fontWeight: 500 },
			'& h4': { fontSize: '0.875rem', mt: 0.5, mb: 0.3, fontWeight: 500 },
			'& p': { mb: 1.5 },
			'& span': { mb: 1, mt: 1, fontSize: '0.875rem', display: 'block' },
			'& ul, & ol': { pl: 2, mb: 1.5 },
			'& li': { mb: 0.5 },
			'& pre': {
				borderRadius: '4px',
				overflowX: 'auto',
				mb: 1.5,
				fontSize: '0.875rem',
			},
			'& code': {
				fontFamily: 'monospace',
				borderRadius: '3px',
				padding: '0.2em 0.4em',
				fontSize: '0.875rem',
			},
			'& blockquote': {
				borderLeft: '4px solid #ddd',
				color: '#666',
				paddingLeft: '16px',
				fontStyle: 'italic',
				mb: 1.5,
			},
			'& table': { borderCollapse: 'collapse', width: '100%', mb: 1.5 },
			'& th, & td': { border: '1px solid #ddd', padding: '8px' },
			'& th': { backgroundColor: '#f5f5f5', textAlign: 'left' },
			'& tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
		}}
	>
		<ReactMarkdown
			components={{
				h1: (props) => (
					<Typography
						component="h1"
						variant="h1"
						{...stripMarkdownProps(props)}
					/>
				),
				h2: (props) => (
					<Typography
						component="h2"
						variant="h2"
						{...stripMarkdownProps(props)}
					/>
				),
				h3: (props) => (
					<Typography
						component="h3"
						variant="h3"
						{...stripMarkdownProps(props)}
					/>
				),
				h4: (props) => (
					<Typography
						component="h4"
						variant="caption"
						{...stripMarkdownProps(props)}
					/>
				),
				p: (props) => (
					<Typography
						component="p"
						variant="caption"
						{...stripMarkdownProps(props)}
					/>
				),
				li: (props) => (
					<Typography
						component="li"
						variant="caption"
						{...stripMarkdownProps(props)}
					/>
				),
				a: (props) => (
					<Link color="primary" {...stripMarkdownProps(props)} />
				),
				code: (props) => {
					const { className, children, ...rest } = stripMarkdownProps(
						props as any,
					);
					const match = /language-(\w+)/.exec(className || '');

					return match ? (
						<PrismHighlighter
							style={vs}
							language={match[1]}
							PreTag="div"
						>
							{String(children).replace(/\n$/, '')}
						</PrismHighlighter>
					) : (
						<code className={className} {...rest}>
							{children}
						</code>
					);
				},
			}}
		>
			{text}
		</ReactMarkdown>
	</Box>
);

const SectionCard: React.FC<{ title?: string; children: React.ReactNode }> = ({
	title,
	children,
}) => (
	<Card variant="outlined" sx={{ mb: 2 }}>
		<CardContent>
			{title && (
				<Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
					{title}
				</Typography>
			)}
			{children}
		</CardContent>
	</Card>
);

const JSONView: React.FC<{ text: string }> = ({ text }) => {
	let pretty = text;
	try {
		pretty = JSON.stringify(JSON.parse(text), null, 2);
	} catch {
		// ignore
	}
	return (
		<SectionCard title="JSON">
			<PrismHighlighter style={vs} language="json" PreTag="div">
				{pretty}
			</PrismHighlighter>
		</SectionCard>
	);
};

function extractRenderable(text: string): {
	mode: 'xml' | 'json' | 'md';
	payload: string;
} {
	const raw = text || '';
	if (isLikelyXML(raw)) return { mode: 'xml', payload: raw };

	if (isLikelyJSON(raw)) {
		try {
			const obj = JSON.parse(raw);
			const candidate =
				typeof obj?.response === 'string' ? obj.response : undefined;
			if (candidate && isLikelyXML(candidate))
				return { mode: 'xml', payload: candidate };
			return { mode: 'json', payload: raw };
		} catch {
			return { mode: 'json', payload: raw };
		}
	}

	return { mode: 'md', payload: raw };
}

const ResponseRenderer: React.FC<ResponseRendererProps> = ({
	text,
	onAction,
	isCurrent,
}) => {
	const { mode, payload } = extractRenderable(text);

	if (mode === 'xml') {
		const parsed = parseTradingXML(payload);
		if (parsed) {
			const { sequence, raw } = parsed;

			return (
				<Box>
					{sequence.map((node, idx) => {
						switch (node.kind) {
							case 'analysis':
								return (
									<AnalysisView
										key={`n-${idx}`}
										node={node}
									/>
								);
							case 'trade_setup':
								return (
									<TradeSetup key={`n-${idx}`} node={node} />
								);
							case 'execution':
								return (
									<ExecutionChat
										key={`n-${idx}`}
										node={node}
									/>
								);
							case 'status_check':
								return (
									<StatusCheck key={`n-${idx}`} node={node} />
								);
							case 'error':
								return (
									<ErrorChat key={`n-${idx}`} node={node} />
								);
							case 'actions':
								return (
									<ActionsView
										key={`n-${idx}`}
										node={node}
										onAction={onAction}
										raw={raw}
										isCurrent={isCurrent}
									/>
								);
							default:
								return null;
						}
					})}
					<Box height={30} />
				</Box>
			);
		}
	}

	if (mode === 'json') return <JSONView text={payload} />;
	return <MarkdownBlock text={payload} />;
};

export default ResponseRenderer;
